'use client';

import { Bell } from 'lucide-react';
import {
  useEventReminder,
  type ReminderEventData,
} from '@/hooks/use-event-reminder';

interface EventReminderButtonProps {
  /** Event slug, used as the persisted reminder state key. */
  slug: string;
  /** Event data used to prefill the Google Calendar invite. */
  event: ReminderEventData;
  /**
   * 'compact' renders the full-width dark button used in the Kajian
   * dropdown; 'large' renders the outline button used on the event page.
   */
  variant?: 'compact' | 'large';
  /** Extra classes merged onto the button, e.g. layout overrides. */
  className?: string;
}

/** Idle and active styles per variant, plus the matching bell size. */
const VARIANT_STYLES = {
  compact: {
    idle:
      'flex w-full items-center justify-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-neutral-700',
    active:
      'flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-200',
    iconSize: 14,
  },
  large: {
    idle:
      'inline-flex items-center gap-2.5 rounded-xl border border-neutral-300 bg-white px-8 py-4 text-base font-bold text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50',
    active:
      'inline-flex items-center gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-8 py-4 text-base font-bold text-amber-800 transition-colors hover:bg-amber-100',
    iconSize: 20,
  },
} as const;

/**
 * "Ingatkan Saya" button that adds the event to Google Calendar and keeps
 * the reminder state per event slug. The bell is gray until the reminder is
 * set, then turns amber and filled to signal the active state.
 */
export function EventReminderButton({
  slug,
  event,
  variant = 'compact',
  className,
}: EventReminderButtonProps) {
  const { isReminded, remind } = useEventReminder(slug, event);
  const styles = VARIANT_STYLES[variant];

  return (
    <button
      type="button"
      onClick={remind}
      aria-pressed={isReminded}
      title={
        isReminded
          ? 'Reminder sudah diaktifkan untuk event ini'
          : 'Tambahkan event ini ke Google Calendar'
      }
      className={`${isReminded ? styles.active : styles.idle} ${className ?? ''}`}
    >
      <Bell
        size={styles.iconSize}
        aria-hidden="true"
        fill={isReminded ? 'currentColor' : 'none'}
        className={isReminded ? 'text-amber-500' : 'text-neutral-400'}
      />
      {isReminded ? 'Reminder Aktif' : 'Ingatkan Saya'}
    </button>
  );
}
